import { Modal } from '@bennows/boows-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { isSearchable, SEARCH_DEBOUNCE_MS } from './useSearch';
import { SearchPanel, type SearchFilters } from './SearchPanel';

/** `id` of the overlay's field; the search page's own field has another one. */
export const SEARCH_OVERLAY_INPUT_ID = 'search-overlay-input';

const NO_FILTERS: SearchFilters = { types: [], archived: false };

/** The overlay's query as a search page link, so a result list can be kept or shared. */
function pageHref(query: string, filters: SearchFilters): string {
  const params = new URLSearchParams();
  params.set('q', query.trim());
  for (const type of filters.types) params.append('types', type);
  if (filters.archived) params.set('archived', '1');
  return `/search?${params.toString()}`;
}

export interface SearchOverlayProps {
  readonly onClose: () => void;
}

/**
 * The search over the current page (style guide 2.10, S150-8): the magnifier of the frame and the
 * `/` shortcut open it with the field focused; full screen on a phone, a wide dialog from 1024 px.
 * Same search as the search page (`SearchPanel`), with the state kept in the overlay; opening a
 * result closes it, and "Auf eigener Seite zeigen" opens the search page with the same query.
 */
export function SearchOverlay({ onClose }: SearchOverlayProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>(NO_FILTERS);
  const opened = useRef(location.key);

  useEffect(() => {
    const handle = setTimeout(() => setQuery(draft), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [draft]);

  // The field takes focus once the dialog is up. The dialog moves focus to its first control (the
  // close button) in a zero timeout of its own, scheduled after ours: so focus one task later.
  useEffect(() => {
    let inner: ReturnType<typeof setTimeout> | undefined;
    const outer = setTimeout(() => {
      inner = setTimeout(() => inputRef.current?.focus(), 0);
    }, 0);
    return () => {
      clearTimeout(outer);
      clearTimeout(inner);
    };
  }, []);

  // A result is a link: once it navigated, the overlay has done its job.
  useEffect(() => {
    if (location.key !== opened.current) onClose();
  }, [location.key, onClose]);

  const changeDraft = (next: string) => {
    setDraft(next);
    if (!next) setQuery('');
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('search.page.title')}
      closeLabel={t('search.overlay.close')}
      layout="wide"
      className="ui-search-overlay"
      footer={
        isSearchable(query) ? (
          <Link className="ui-btn ui-btn--secondary" to={pageHref(query, filters)}>
            {t('search.overlay.openPage')}
          </Link>
        ) : null
      }
    >
      <div className="ui-stack">
        <SearchPanel
          inputId={SEARCH_OVERLAY_INPUT_ID}
          inputRef={inputRef}
          draft={draft}
          onDraftChange={changeDraft}
          query={query}
          onSubmit={() => setQuery(draft)}
          filters={filters}
          onFiltersChange={(change) => setFilters((current) => ({ ...current, ...change }))}
        />
      </div>
    </Modal>
  );
}
